import { CHECKOUT_ITEM_TYPE_EXPERIENCE, CHECKOUT_ITEM_TYPE_TRANSFER } from 'constants/checkout'
import { sum } from 'lib/array/arrayUtils'
import { isExperienceTicket } from 'lib/experiences/experienceUtils'
import uuidV4 from 'lib/string/uuidV4Utils'

export interface ExperienceTicketCount {
  ticket: App.ExperienceItemTicket | App.PackageAddonItem
  count: number
}

export interface ExperienceDetails {
  date?: string
  time?: string
  language?: string
  pickupPoint?: string
  redemptionLocation?: string
  ticketModesMap?: Record<string, string>
}

export function generateExperienceCheckoutTicket(item: ExperienceTicketCount): App.Checkout.ExperienceItemTicket {
  const ticket = item.ticket
  if (isExperienceTicket(ticket)) {
    return {
      ticketId: ticket.id,
      name: ticket.name,
      count: item.count,
      price: ticket.price,
      memberPrice: ticket.memberPrice,
      discount: ticket.discounts.app.amount,
      date: ticket.date,
      orderItemIds: ticket.orderItemIds,
    }
  } else {
    return {
      ticketId: ticket.idSalesforceExternal,
      name: ticket.fareTypeText,
      count: item.count,
      price: ticket.price,
      memberPrice: 0,
      discount: 0,
    }
  }
}

interface ExperienceItemOptions {
  isBookingBNBL?: boolean
  cartItem?: App.Checkout.ExperienceItem
  isBuyNowBookLater?: boolean
  isGift?: boolean
  orderMode?: App.ExperienceOrderMode
  isAccomBundle?: string
}

export function generateExperienceCheckoutItem(
  experience: App.ExperienceOffer,
  details: ExperienceDetails,
  tickets: Array<ExperienceTicketCount>,
  options: ExperienceItemOptions = {},
): App.Checkout.ExperienceItem {
  const { cartItem, isBookingBNBL, isBuyNowBookLater, isGift, orderMode, isAccomBundle } = options
  const totalPrice = sum(tickets, ticketCount => ticketCount.ticket.price)
  const memberTotal = sum(tickets, ticketCount => ticketCount.ticket.memberPrice ?? 0)
  const item: App.Checkout.ExperienceItem = {
    itemId: cartItem?.itemId || uuidV4(),
    transactionKey: cartItem?.transactionKey || uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_EXPERIENCE,
    bookingType: experience.bookingType,
    tickets: tickets.map(generateExperienceCheckoutTicket),
    experienceId: experience.id,
    leExclusive: experience.leExclusive,
    date: details.date,
    isBuyNowBookLater: isBuyNowBookLater ?? false,
    isGift: !!isGift,
    orderMode,
    isBookingBNBL,
    isAccomBundle,
    time: details.time,
    languageId: details.language,
    pickupPointId: details.pickupPoint,
    redemptionLocationId: details.redemptionLocation,
    ticketModesMap: details.ticketModesMap,
    total: totalPrice,
    memberTotal,
  }

  return item
}

interface BundledTransferInput {
  experience: App.ExperienceOffer
  option: App.ExperienceTransferOption
  date: string
  hotel: App.ExperienceTransferView['hotel']
  travellers: App.ExperienceTransferTravellers
  baggage: App.OrderTransferItemBaggage
}

export function generateBundledAccommodationExperienceItem(
  bundleId: string,
  ticket: { experienceId: string; productId: string },
): App.Checkout.ExperienceItem
export function generateBundledAccommodationExperienceItem(
  bundleId: string,
  ticket: { experienceId: string; productId: string },
  transferData: BundledTransferInput,
): App.Checkout.TransferItem
export function generateBundledAccommodationExperienceItem(
  bundleId: string,
  ticket: { experienceId: string; productId: string },
  transferData?: BundledTransferInput,
): App.Checkout.ExperienceItem | App.Checkout.TransferItem {
  if (transferData) {
    const { experience, option, date, hotel, travellers, baggage } = transferData
    const transferItem = generateTransferCheckoutItem(experience, {
      option,
      date,
      time: '',
      flightNumber: '',
      title: experience.name,
      type: option.transferTypes[0],
      isHomePickup: false,
      hotel,
      travellers,
      baggage,
    })
    return { ...transferItem, isAccomBundle: bundleId }
  }
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_EXPERIENCE,
    experienceId: ticket.experienceId,
    tickets: [{
      ticketId: ticket.productId,
      name: 'Bundled experience',
      count: 1,
      price: 0,
      memberPrice: 0,
      discount: 0,
    }],
    leExclusive: false,
    isBuyNowBookLater: false,
    isGift: false,
    isAccomBundle: bundleId,
  }
}

export function generateTransferCheckoutItem(
  experience: App.ExperienceOffer,
  transfer: App.ExperienceTransferView,
  cartItem?: App.Checkout.TransferItem,
): App.Checkout.TransferItem {
  const item: App.Checkout.TransferItem = {
    itemId: cartItem?.itemId || uuidV4(),
    transactionKey: cartItem?.transactionKey || uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_TRANSFER,
    transfer,
    experienceId: experience.id,
    offerType: experience.productType,
    hasLoyaltyBenefit: cartItem?.hasLoyaltyBenefit,
    isLoyaltyTransfer: cartItem?.isLoyaltyTransfer,
  }

  return item
}

export function generateLoyaltyLoungePassItem(
  experience: App.ExperienceOffer,
  ticket: App.ExperienceItemTicket,
  options: {
    date?: string
    time?: string
    isBookLater?: boolean
    isFree?: boolean
  } = {},
): App.Checkout.LoyaltyLoungePassItem {
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: 'loungePassItem',
    experienceId: experience.id,
    ticketId: ticket.id,
    isBookLater: !!options.isBookLater,
    isFree: !!options.isFree,
    date: options.date,
    time: options.time,
    redemptionLocation: experience.redemptionLocations[0].id,
  }
}
